/**
 * RecordEditorPage — full-page record editor for tabbed / multi-step forms.
 *
 * Used when the form's layout_type is `tabs` or `stepper`. Simple single-layout
 * forms still use the dialog editor inside FormRecordsPage.
 *
 * Routes:
 *   /forms/:id/records/new
 *   /forms/:id/records/:recordId/edit
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageContainer } from "@/components/app/PageShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DynamicFormRenderer } from "@/components/app/DynamicFormRenderer";
import { RecordCommentsThread } from "@/components/app/RecordCommentsThread";
import { ArrowLeft, FloppyDisk, ChatCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function RecordEditorPage() {
    const { id, recordId } = useParams();
    const isNew = !recordId;
    const navigate = useNavigate();
    const { user } = useAuth();

    const [form, setForm] = useState(null);
    const [sections, setSections] = useState([]);
    const [fields, setFields] = useState([]);
    const [groupDefaults, setGroupDefaults] = useState({});
    const [record, setRecord] = useState(null);
    const [recData, setRecData] = useState({});
    const [perms, setPerms] = useState({ can_create: false, can_update: false });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showComments, setShowComments] = useState(false);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const [{ data: meta }, permsRes, defaultsRes] = await Promise.all([
                    api.get(`/forms/${id}`),
                    api.get(`/forms/${id}/permissions`).catch(() => ({ data: { can_create: true, can_update: true } })),
                    api.get(`/forms/${id}/records/my-defaults`).catch(() => ({ data: {} })),
                ]);
                setForm(meta.form);
                setSections(meta.sections || []);
                setFields(meta.fields || []);
                setPerms(permsRes.data || {});

                const gDefaults = defaultsRes.data || {};
                setGroupDefaults(gDefaults);

                if (recordId) {
                    const { data: r } = await api.get(`/forms/${id}/records/${recordId}`);
                    setRecord(r); setRecData(r.data || {});
                } else {
                    const seed = {};
                    (meta.fields || []).forEach((field) => {
                        if (field.default_group_value_enabled && gDefaults[field.field_name] !== undefined) {
                            seed[field.field_name] = gDefaults[field.field_name];
                        } else if (field.default_value != null) {
                            seed[field.field_name] = field.default_value;
                        }
                    });
                    setRecData(seed);
                }
            } catch (e) {
                toast.error(extractError(e));
                navigate(`/forms/${id}/records`);
            } finally { setLoading(false); }
        })();
    }, [id, recordId, navigate, user]);

    const wfStepControls = useMemo(() => {
        if (!record || !form?.workflow_enabled) return { field: null, section: null };
        if (record.workflow_status !== "pending") return { field: null, section: null };
        const steps = form.workflow_steps || [];
        const idx = record.workflow_current_step ?? 0;
        const step = steps[idx];
        return {
            field: step?.field_controls && Object.keys(step.field_controls).length ? step.field_controls : null,
            section: step?.section_controls && Object.keys(step.section_controls).length ? step.section_controls : null,
        };
    }, [record, form]);

    const save = async () => {
        setSaving(true);
        try {
            if (isNew) {
                await api.post(`/forms/${id}/records`, { data: recData, visibility: "everyone" });
                toast.success("Record created");
            } else {
                await api.patch(`/forms/${id}/records/${recordId}`, { data: recData });
                toast.success("Record updated");
            }
            navigate(`/forms/${id}/records`);
        } catch (e) { toast.error(extractError(e)); }
        finally { setSaving(false); }
    };

    if (loading) {
        return <PageContainer><div className="text-sm text-muted-foreground">Loading…</div></PageContainer>;
    }
    if (!form) return null;
    const canSave = isNew ? perms.can_create : perms.can_update;
    const layoutMode = form?.layout_type || form?.layout_mode || "single";
    const wfStatus = record?.workflow_status;

    return (
        <PageContainer>
            {/* Sticky toolbar */}
            <div className="sticky top-0 z-20 -mx-4 px-4 py-3 bg-background/85 backdrop-blur border-b border-border mb-6" data-testid="record-editor-toolbar">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                        <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => navigate(`/forms/${id}/records`)} data-testid="record-editor-back">
                            <ArrowLeft size={14} className="mr-1" /> Back
                        </Button>
                        <div className="min-w-0">
                            <div className="label-overline truncate">{isNew ? "New record" : "Edit record"}</div>
                            <h1 className="text-xl font-bold truncate" data-testid="record-editor-title">{form.form_title || form.form_name}</h1>
                        </div>
                        <Badge variant="outline" className="rounded-sm font-mono text-[10px] uppercase">{layoutMode}</Badge>
                        {wfStatus && (
                            <Badge variant="outline" className={`rounded-sm font-mono text-[10px] capitalize ${wfStatus === "approved" ? "border-success text-success" : wfStatus === "rejected" ? "border-destructive text-destructive" : "border-warning text-warning"}`}>
                                {wfStatus}
                            </Badge>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        {!isNew && (
                            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setShowComments((v) => !v)} data-testid="record-editor-comments-toggle">
                                <ChatCircle size={14} className="mr-1" /> {showComments ? "Hide" : "Show"} comments
                            </Button>
                        )}
                        <Button onClick={save} disabled={!canSave || saving} className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="record-editor-save">
                            <FloppyDisk size={14} className="mr-1" /> {saving ? "Saving…" : isNew ? "Create record" : "Save changes"}
                        </Button>
                    </div>
                </div>
            </div>

            <div className={showComments && !isNew ? "grid lg:grid-cols-[1fr_360px] gap-6" : ""}>
                <div className="min-w-0">
                    <DynamicFormRenderer
                        sections={sections}
                        fields={fields}
                        data={recData}
                        onChange={setRecData}
                        fieldControls={wfStepControls.field}
                        sectionControls={wfStepControls.section}
                        layoutMode={layoutMode}
                        formId={id}
                        recordId={isNew ? null : recordId}
                    />
                </div>
                {showComments && !isNew && (
                    <aside className="brut-section p-4 lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] overflow-y-auto" data-testid="record-editor-comments">
                        <div className="label-overline mb-3">Comments</div>
                        <RecordCommentsThread recordId={recordId} />
                    </aside>
                )}
            </div>
        </PageContainer>
    );
}
